/**
 * Notification API enhancements.
 *
 * The generated `getNotifications`/`markRead`/`markAllRead`/`deleteNotifications` endpoints
 * carry no RTK Query cache tags, so nothing ever told the notification bell or the full inbox
 * to refresh after a mutation — a notification stayed visually "unread" until the next full
 * page reload happened to refetch it.
 *
 * Tags fix that, but a tag-triggered refetch is still a network round-trip: clicking a
 * notification should look read the instant it's clicked, not once a response comes back. So
 * every mutation here also optimistically patches every currently-cached `getNotifications`
 * page (the bell dropdown and the full inbox page use different `{page, size}` args, so there
 * can be more than one cached page at once — `selectCachedArgsForQuery` finds all of them) and
 * rolls the patch back if the request ultimately fails. Tags remain as the eventual-consistency
 * safety net (e.g. a `{page, size}` combination that isn't currently cached yet).
 */
import { api } from './baseApi'
import { engineApi } from './engineApi'
import type { GetNotificationsResponse } from './notificationTypes'

type NotificationsRootState = Parameters<typeof engineApi.util.selectCachedArgsForQuery>[0]

/** A minimal structural type for the `MutationLifecycleApi` bits this file needs. */
interface NotificationLifecycleApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts any RTK Query thunk action, see patchAllNotificationPages doc comment
  dispatch: (action: any) => any
  getState: () => NotificationsRootState
  queryFulfilled: Promise<unknown>
}

/**
 * Applies `updater` to every currently-cached `getNotifications` result and returns the applied
 * patches' `undo` functions so the caller can roll them back if the mutation fails.
 *
 * Uses `engineApi` (rather than the `enhancedApi` this file builds below) purely for its fully
 * typed `Definitions` — `enhanceEndpoints()`'s return type doesn't carry the widened endpoint
 * map needed by `selectCachedArgsForQuery`/`updateQueryData`'s generics, but both objects mutate
 * and read the exact same underlying RTK Query cache slice at runtime.
 */
function patchAllNotificationPages(
  { dispatch, getState }: Pick<NotificationLifecycleApi, 'dispatch' | 'getState'>,
  updater: (draft: GetNotificationsResponse) => void
): Array<() => void> {
  const cachedArgs = engineApi.util.selectCachedArgsForQuery(getState(), 'getNotifications')
  return cachedArgs.map(
    (args) =>
      dispatch(
        engineApi.util.updateQueryData('getNotifications', args, (draft) =>
          updater(draft as GetNotificationsResponse)
        )
      ).undo
  )
}

api.enhanceEndpoints({
  addTagTypes: ['Notifications'],
  endpoints: {
    getNotifications: {
      providesTags: ['Notifications'],
    },
    markRead: {
      invalidatesTags: ['Notifications'],
      async onQueryStarted(
        arg: { notificationId: string },
        lifecycleApi: NotificationLifecycleApi
      ) {
        const { notificationId } = arg
        const { queryFulfilled } = lifecycleApi
        const undoPatches = patchAllNotificationPages(lifecycleApi, (draft) => {
          const notification = draft.notifications?.find((n) => n.notificationId === notificationId)
          if (notification && !notification.read) {
            notification.read = true
            if (typeof draft.unreadCount === 'number') {
              draft.unreadCount = Math.max(0, draft.unreadCount - 1)
            }
          }
        })
        try {
          await queryFulfilled
        } catch {
          undoPatches.forEach((undo) => undo())
        }
      },
    },
    markAllRead: {
      invalidatesTags: ['Notifications'],
      async onQueryStarted(_arg: void, lifecycleApi: NotificationLifecycleApi) {
        const { queryFulfilled } = lifecycleApi
        const undoPatches = patchAllNotificationPages(lifecycleApi, (draft) => {
          draft.notifications?.forEach((n) => {
            n.read = true
          })
          draft.unreadCount = 0
        })
        try {
          await queryFulfilled
        } catch {
          undoPatches.forEach((undo) => undo())
        }
      },
    },
    deleteNotifications: {
      invalidatesTags: ['Notifications'],
      async onQueryStarted(
        arg: { deleteNotificationsBody: { notificationIds?: string[] } },
        lifecycleApi: NotificationLifecycleApi
      ) {
        const { deleteNotificationsBody } = arg
        const { queryFulfilled } = lifecycleApi
        const ids = new Set(deleteNotificationsBody.notificationIds ?? [])
        const undoPatches = patchAllNotificationPages(lifecycleApi, (draft) => {
          if (!draft.notifications) return
          const removedUnreadCount = draft.notifications.filter(
            (n) => ids.has(n.notificationId) && !n.read
          ).length
          const remaining = draft.notifications.filter((n) => !ids.has(n.notificationId))
          const removedCount = draft.notifications.length - remaining.length
          draft.notifications = remaining
          if (typeof draft.total === 'number') {
            draft.total = Math.max(0, draft.total - removedCount)
          }
          if (typeof draft.unreadCount === 'number') {
            draft.unreadCount = Math.max(0, draft.unreadCount - removedUnreadCount)
          }
        })
        try {
          await queryFulfilled
        } catch {
          undoPatches.forEach((undo) => undo())
        }
      },
    },
  },
})

export {
  useGetNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useDeleteNotificationsMutation,
} from './engineApi'
